import algoliasearch from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import {
  index,
  pagination,
  searchBox,
  stats,
  hits,
  configure,
} from "instantsearch.js/es/widgets";

declare global {
  var ALGOLIA_APP_ID: string;
  var ALGOLIA_SEARCH_API_KEY: string;
  var ALGOLIA_INDEX_NAME: string;
  var SITE_ID: number;
  var CHEVRON: string;
  var PREV: string;
  var NEXT: string;
  var NO_RESULTS: string;
  var RESULT: string;
  var RESULTS: string;
  var FUND_DETAILS: string;
  var ETF_QUICK_LINKS: { url: string; title: string }[];
}

const searchClient = algoliasearch(
  globalThis.ALGOLIA_APP_ID,
  globalThis.ALGOLIA_SEARCH_API_KEY
);

const searchInput: HTMLElement | null =
  document.querySelector(".js-search-input");
const resultsEtfPagination: HTMLElement | null =
  document.querySelector(".js-etf-pagination");
const resultsContentPagination: HTMLElement | null = document.querySelector(
  ".js-content-pagination"
);
const etfResults: HTMLElement | null =
  document.querySelector(".js-etf-results");
const contentResults: HTMLElement | null = document.querySelector(
  ".js-content-results"
);
const etfCount: HTMLElement | null = document.querySelector(".js-etf-count");
const contentCount: HTMLElement | null =
  document.querySelector(".js-content-count");
const etfCountNumber: HTMLElement | null = document.querySelector(
  ".js-etf-count-number"
);
const contentCountNumber: HTMLElement | null = document.querySelector(
  ".js-content-count-number"
);

const searchInit = () => {
  if (
    !(resultsEtfPagination instanceof Element) ||
    !(resultsContentPagination instanceof Element)
  ) {
    return;
  }

  if (!(searchInput instanceof Element)) {
    return;
  }

  if (
    !(etfResults instanceof Element) ||
    !(contentResults instanceof Element)
  ) {
    return;
  }

  if (!(etfCount instanceof Element) || !(contentCount instanceof Element)) {
    return;
  }

  if (
    !(etfCountNumber instanceof Element) ||
    !(contentCountNumber instanceof Element)
  ) {
    return;
  }

  const search = instantsearch({
    indexName: globalThis.ALGOLIA_INDEX_NAME,
    searchClient,
    routing: {
      stateMapping: {
        stateToRoute(uiState) {
          const indexUiState = uiState[globalThis.ALGOLIA_INDEX_NAME];
          return {
            q: indexUiState.query,
          };
        },
        routeToState(routeState) {
          return {
            [globalThis.ALGOLIA_INDEX_NAME]: {
              query: routeState.q,
            },
          };
        },
      },
    },
  });

  const configureOptions = {
    hitsPerPage: 10,
    filters: `siteId=${globalThis.SITE_ID}`,
    ignorePlurals: true,
  };

  const statsOptions = {
    templates: {
      text: `
      {{#hasNoResults}}${globalThis.NO_RESULTS}{{/hasNoResults}}
      {{#hasOneResult}}1 ${globalThis.RESULT}{{/hasOneResult}}
      {{#hasManyResults}}{{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}} ${globalThis.RESULTS}{{/hasManyResults}}
      `,
    },
  };

  const numberOptions = {
    templates: {
      text: `
      {{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}}
      `,
    },
  };

  const paginationOptions = {
    showFirst: false,
    showPrevious: true,
    showNext: true,
    showLast: false,
    padding: 1,
    templates: {
      next: globalThis.NEXT,
      previous: globalThis.PREV,
    },
  };

  const searchbox = searchBox({
    container: searchInput,
    searchAsYouType: false,
    cssClasses: {
      form: "results__search-box__form",
      input: "results__search-box__input",
      submit: "results__search-box__submit",
      submitIcon: "results__search-box__submit-icon",
      reset: "results__search-box__reset",
      resetIcon: "results__search-box__reset-icon",
    },
  });

  search.addWidgets([
    searchbox,
    configure({
      facetFilters: ["section:ETFs"],
      ...configureOptions,
    }),
    stats({
      container: etfCount,
      ...statsOptions,
    }),
    stats({
      container: etfCountNumber,
      ...numberOptions,
    }),
    hits({
      container: etfResults,
      templates: {
        item: `
        <article class="card-news" x-data="{showFundDetails: false}">
            <div class="card-news__copy">
              <a href="{{ url }}" class="card-news__link">
                <span class="card-news__title ml-0"> {{ title }} </span>
              </a>

              <button class="card-news__fund-details-link" :class="showFundDetails && 'card-news__fund-details-link--open'" @click="showFundDetails = !showFundDetails">${
                globalThis.FUND_DETAILS
              } ${globalThis.CHEVRON}</button>
              {{#bmoGamSlug}}
                <div class="card-news__block fund-details" x-show="showFundDetails">
                  ${Object.values(globalThis.ETF_QUICK_LINKS)
                    .map((fundLink) => {
                      return `<a href="${
                        fundLink.url.split("{bmoGamSlug}")[0]
                      }{{ bmoGamSlug }}${
                        fundLink.url.split("{bmoGamSlug}")[1]
                      }">${fundLink.title}${globalThis.CHEVRON}</a>`;
                    })
                    .join("")}
                </div>
              {{/bmoGamSlug}}
            </div>
      </article>
        `,
        empty: ``,
      },
    }),
    pagination({
      container: resultsEtfPagination,
      scrollTo: etfResults.closest(".results__container") as HTMLElement,
      ...paginationOptions,
    }),
    index({
      indexId: "content",
      indexName: globalThis.ALGOLIA_INDEX_NAME,
    }).addWidgets([
      configure({
        facetFilters: ["section:-ETFs"],
        ...configureOptions,
      }),
      stats({
        container: contentCount,
        ...statsOptions,
      }),
      stats({
        container: contentCountNumber,
        ...numberOptions,
      }),
      hits({
        container: contentResults,
        templates: {
          item: `
          <article class="card-news">
              <div class="card-news__copy">
                {{#section}}
                  <span class="card-news__eyebrow">{{ section }}</span>
                {{/section}}
                <a href="{{ url }}" class="card-news__link">
                  <span class="card-news__title ml-0"> {{ title }}</span>
                </a>

                <div class="card-news__text">
                  {{#referenceDescription}}
                    <p>{{ referenceDescription }}</p>
                  {{/referenceDescription}}
                  {{^referenceDescription}}
                    <p>{{ body }}</p>
                  {{/referenceDescription}}
                </div>

                <div class="card-news__block">
                  <span class="card-news__source">
                  </span>
                </div>
              </div>
        </article>
          `,
          empty: ``,
        },
      }),
      pagination({
        container: resultsContentPagination,
        scrollTo: contentResults.closest(".results__container") as HTMLElement,
        ...paginationOptions,
      }),
    ]),
  ]);

  search.start();

  search.on("render", () => {
    if (etfCountNumber.innerText && contentCountNumber.innerText) {
      window.dispatchEvent(
        new CustomEvent("algolia-rendered", {
          detail: {
            etfCount: etfCountNumber.innerText,
            contentCount: contentCountNumber.innerText,
          },
        })
      );
    }
  });
};

searchInit();
